import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Star, Check, X, Trash2, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReviews = () => {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/reviews`, getAuthHeaders()),
        axios.get(`${API_URL}/admin/reviews/stats`, getAuthHeaders())
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(`${API_URL}/admin/reviews/${reviewId}/approve`, {}, getAuthHeaders());
      toast.success('Review approved');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await axios.put(`${API_URL}/admin/reviews/${reviewId}/reject`, {}, getAuthHeaders());
      toast.success('Review rejected');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to reject review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await axios.delete(`${API_URL}/admin/reviews/${reviewId}`, getAuthHeaders());
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const filteredReviews = reviews.filter(review => 
    filter === 'all' || review.status === filter
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">User Reviews</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-gray-500 text-sm">Total Reviews</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-gray-500 text-sm">Pending Approval</p>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
            <p className="text-gray-500 text-sm">Approved</p>
          </CardContent>
        </Card>
        <Card className="border-saffron-200">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="w-6 h-6 fill-saffron-500 text-saffron-500" />
              <p className="text-3xl font-bold text-saffron-600">{stats.average_rating}</p>
            </div>
            <p className="text-gray-500 text-sm">Average Rating</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-saffron-500' : ''}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && stats.pending > 0 && (
              <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full">{stats.pending}</span>
            )}
          </Button>
        ))}
      </div>

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Reviews ({filteredReviews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading...</div>
          ) : filteredReviews.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No reviews found</div>
          ) : (
            <div className="space-y-4">
              {filteredReviews.map(review => (
                <div key={review.id} className={`border rounded-lg p-4 ${review.status === 'pending' ? 'border-yellow-200 bg-yellow-50/50' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-saffron-100 rounded-full flex items-center justify-center">
                          <span className="text-saffron-600 font-semibold">
                            {review.user_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{review.user_name}</p>
                          <p className="text-sm text-gray-500">{review.user_email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(review.rating)}
                        <Badge variant="outline">{review.feature}</Badge>
                        {getStatusBadge(review.status)}
                      </div>
                      
                      <p className="text-gray-700 mb-2">{review.review}</p>
                      
                      <p className="text-xs text-gray-400">
                        {new Date(review.created_at).toLocaleString()}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {review.status === 'pending' && (
                        <>
                          <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => handleApprove(review.id)}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500 border-red-200" onClick={() => handleReject(review.id)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(review.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReviews;
